import type { NetWorthPoint } from "@/lib/api/portfolio";
import type { GoalProjectionPoint } from "@/lib/api/portfolio";
import type { GoalRow } from "@/components/goals/GoalDeviationChart";
import type { GoalEntry } from "@/components/goals/EditGoalsDialog";

export function buildGoalRows(
  goals: GoalEntry[],
  history: NetWorthPoint[],
  projectionPoints: GoalProjectionPoint[],
  includeProperties: boolean,
  currentYear: number
): GoalRow[] {
  const projectionMap = Object.fromEntries(projectionPoints.map((p) => [p.year, p]));

  return [...goals]
    .sort((a, b) => a.year - b.year)
    .map(({ year, targetAmount }) => {
      let actual: number | null = null;

      if (year < currentYear) {
        const yearPoints = history.filter((pt) => pt.date.startsWith(String(year)));
        const point = yearPoints.at(-1);
        if (point) {
          actual = includeProperties ? point.value : point.value - (point.properties ?? 0);
        }
      } else {
        const proj = projectionMap[year];
        if (proj) {
          actual = includeProperties ? proj.value : proj.value - proj.properties;
        } else {
          const lastPoint = history.at(-1);
          if (lastPoint) {
            actual = includeProperties
              ? lastPoint.value
              : lastPoint.value - (lastPoint.properties ?? 0);
          }
        }
      }

      const diff = actual !== null ? actual - targetAmount : null;
      const deviationPct =
        actual !== null && targetAmount > 0
          ? ((actual - targetAmount) / targetAmount) * 100
          : null;
      const status: GoalRow["status"] =
        deviationPct === null
          ? null
          : deviationPct >= 0
          ? "ahead"
          : deviationPct >= -10
          ? "on-track"
          : "behind";

      return { year, target: targetAmount, actual, diff, deviationPct, status };
    });
}

export interface CompoundPoint {
  year: number;
  contributed: number;
  interest: number;
  total: number;
}

export interface CompoundResult {
  finalValue: number;
  totalContributed: number;
  totalInterest: number;
  points: CompoundPoint[];
}

export function computeCompound(params: {
  initialCapital: number;
  annualRate: number;
  years: number;
  periodicContribution: number;
  frequency: "monthly" | "annual";
}): CompoundResult {
  const { initialCapital, annualRate, years, periodicContribution, frequency } = params;
  const ppy = frequency === "monthly" ? 12 : 1;
  const r = annualRate / 100 / ppy;
  const points: CompoundPoint[] = [];

  for (let y = 0; y <= Math.max(0, Math.ceil(years)); y++) {
    const n = y * ppy;
    const contributed = initialCapital + periodicContribution * n;
    const total =
      r === 0
        ? contributed
        : initialCapital * (1 + r) ** n + (periodicContribution * ((1 + r) ** n - 1)) / r;
    points.push({ year: y, contributed, total, interest: Math.max(0, total - contributed) });
  }

  const last = points.at(-1)!;
  return {
    finalValue: last.total,
    totalContributed: last.contributed,
    totalInterest: last.interest,
    points,
  };
}

export function solveInterestRate(params: {
  targetValue: number;
  initialCapital: number;
  years: number;
  periodicContribution: number;
  frequency: "monthly" | "annual";
}): number | null {
  const { targetValue, initialCapital, years, periodicContribution, frequency } = params;
  const ppy = frequency === "monthly" ? 12 : 1;
  const n = years * ppy;

  function fvAt(annualRate: number): number {
    const r = annualRate / 100 / ppy;
    if (r === 0) return initialCapital + periodicContribution * n;
    return initialCapital * (1 + r) ** n + (periodicContribution * ((1 + r) ** n - 1)) / r;
  }

  if (fvAt(0) >= targetValue) return 0;

  let hi = 100;
  while (fvAt(hi) < targetValue && hi < 10_000) hi *= 2;
  if (fvAt(hi) < targetValue) return null;

  let lo = 0;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (fvAt(mid) < targetValue) lo = mid;
    else hi = mid;
    if (hi - lo < 0.0001) break;
  }
  return (lo + hi) / 2;
}

export function solveDuration(params: {
  targetValue: number;
  initialCapital: number;
  annualRate: number;
  periodicContribution: number;
  frequency: "monthly" | "annual";
}): number | null {
  const { targetValue, initialCapital, annualRate, periodicContribution, frequency } = params;
  if (targetValue <= initialCapital) return 0;

  const ppy = frequency === "monthly" ? 12 : 1;
  const r = annualRate / 100 / ppy;

  let periods: number;
  if (r === 0) {
    if (periodicContribution <= 0) return null;
    periods = (targetValue - initialCapital) / periodicContribution;
  } else {
    const num = targetValue * r + periodicContribution;
    const den = initialCapital * r + periodicContribution;
    if (den <= 0 || num <= 0) return _solveDurationNumerical(targetValue, initialCapital, r, periodicContribution, ppy);
    periods = Math.log(num / den) / Math.log(1 + r);
  }

  if (periods < 0) return 0;
  return periods / ppy;
}

function _solveDurationNumerical(target: number, pv: number, r: number, pmt: number, ppy: number): number | null {
  function fv(n: number): number {
    if (r === 0) return pv + pmt * n;
    return pv * (1 + r) ** n + (pmt * ((1 + r) ** n - 1)) / r;
  }
  let lo = 0, hi = 600 * ppy;
  if (fv(hi) < target) return null;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (fv(mid) < target) lo = mid;
    else hi = mid;
    if (hi - lo < 0.1) break;
  }
  return hi / ppy;
}

export function solveContribution(params: {
  targetValue: number;
  initialCapital: number;
  annualRate: number;
  years: number;
  frequency: "monthly" | "annual";
}): number | null {
  const { targetValue, initialCapital, annualRate, years, frequency } = params;
  const ppy = frequency === "monthly" ? 12 : 1;
  const n = years * ppy;
  const r = annualRate / 100 / ppy;

  if (n <= 0) return null;
  if (r === 0) return Math.max(0, (targetValue - initialCapital) / n);

  const factor = (1 + r) ** n;
  return Math.max(0, ((targetValue - initialCapital * factor) * r) / (factor - 1));
}
